export default {
  common: {
    back: "Back",
    cancel: "Cancel",
    submit: "Submit",
    loading: "Loading",
    noData: "No data",
    copySuccess: "Copied!",
  },
  index: {
    title: "AI Models",
    cards: {
      gasfee: {
        title: "First Gas Fee Tracing",
        desc: "Trace exchange addresses within 5 levels for a single address",
      },
      multilayer: {
        title: "Multi-layer Tracing",
        desc: "Trace up to 3 levels of inflow or outflow paths for one address",
      },
      transactionpath: {
        title: "Transaction Path Analysis",
        desc: "Analyze waypoints and paths from multiple start addresses to end addresses within 2 levels",
      },
      associatedGasFee: {
        title: "Associated Gas Fee Tracing",
        desc: "Identify addresses associated with gas fees for investigation",
      },
      tornado: {
        title: "Tornado Model",
        desc: "Intelligently trace mixing platform data with bidirectional forensics",
      },
      newOldStop: {
        title: "New-Old Stop Model",
        desc: "Automatically track illicit fund migration from old to new addresses",
        tooltip:
          "The New-Old Stop Model is used to analyze the migration behavior of illicit addresses. Users can specify target addresses and filtering conditions, and the system will automatically retrieve the earliest or latest transaction of that address to identify whether the address is activated or deactivated. The model can discover complete fund transfers when illicit actors change their receiving addresses, helping to track their latest active addresses or trace back to historical addresses, providing key clues for on-chain tracking and risk analysis.",
      },
      singleChannel: {
        title: "Single Channel Transfer Model",
        desc: "Automatically track upstream/downstream fund flows and identify single-channel transfer paths",
        tooltip:
          "This model is used to identify transfer behavior in the process of illicit fund movement. The system starts from a specified address and analyzes its fund inflow and outflow relationships layer by layer, searching for address chains with highly concentrated fund flows. The model continuously tracks along these highly dependent transfer paths until the funds show dispersed flow, thereby revealing the main transfer paths of illicit funds.",
      },
    },
  },
  gasfee: {
    title: "First Gas Fee Tracing",
    empty: {
      title: "No data",
      tip: "Select nodes on the canvas and right-click or use the AI Models menu to choose First Gas Fee Tracing",
    },
    basicInfo: {
      verifyType: "Verification Type",
      feeModel: "Gas Fee Model",
    },
    result: {
      titlePrefix: "Result:",
      firstSourceLevel: "First gas fee source level",
      sourceLevel: "Gas fee source level",
      firstSourcePlatform: "First gas fee source platform",
      firstSourceAddress: "First gas fee source address",
      sourceAddress: "Gas fee source address",
      firstSourceHash: "First gas fee source hash",
      sourceHash: "Gas fee source hash",
      firstSourceTime: "First gas fee source time",
      sourceTime: "Gas fee source time",
      firstSourceQuantity: "First gas fee source amount",
      sourceQuantity: "Gas fee source amount",
    },
    survey: {
      add: "Add Survey",
      normal: "Normal Survey",
      important: "Key Survey",
    },
  },
  multilayer: {
    title: "Multi-layer Tracing",
    form: {
      addressLabel: "Address for analysis",
      addressPlaceholder: "Enter address",
      currencyLabel: "Currency",
      currencyPlaceholder: "Select currency",
      directionLabel: "Direction",
      directionPlaceholder: "Select direction",
      directionOptions: {
        OUTFLOW: "Outflow",
        INFLOW: "Inflow",
      },
      levelLabel: "Association level",
      levelPlaceholder: "Select level",
      cancel: "Cancel",
      submit: "Submit",
    },
    result: {
      analysisAddress: "[Address for analysis]",
      calculationCurrency: "[Currency]",
      analysisBanner: "Result: Traced {count} transaction paths",
      graphAnalysis: "Graph Analysis",
      level: "Level:",
      tag: "Tag:",
      levelTotalQuantityDesc: "Total amount at level {depth} of current path",
      outflow: "Outflow",
      inflow: "Inflow",
      traceTo: "Trace to:",
      levelUnit: " level",
      levelTransaction: " level transaction count",
    },
  },
  transactionpath: {
    title: "Transaction Path Analysis",
    form: {
      fromAddressLabel: "Start addresses",
      multiTip: "Separate multiple addresses with new lines (ctrl/⌘+enter)",
      fromAddressPlaceholder: "Enter up to 5 addresses",
      toAddressLabel: "End addresses",
      toAddressPlaceholder: "Enter up to 5 addresses",
      swapTip: "Swap start/end",
      currencyTypeLabel: "Currency type",
      currencyPlaceholder: "Select currency",
      associationLabel: "Association level",
      associationPlaceholder: "Select region",
      cancel: "Cancel",
      submit: "Submit",
      validators: {
        currencyRequired: "Please select currency",
        fromRequired: "Please enter start addresses",
        toRequired: "Please enter end addresses",
        maxFive: "You can enter up to 5 addresses",
      },
      clear: "Clear",
    },
  },
  transactiontabel: {
    title: "Transaction Path Analysis",
    summary: {
      startAddresses: "[Start addresses]",
      endAddresses: "[End addresses]",
      completionTime: "[Completion time]",
      calculationCurrency: "[Currency]",
      calculationLevel: "[Level]",
    },
    result: {
      summary: "Result: {pathCount} paths, {wayPointCount} waypoints involved",
    },
    table: {
      waypoint: "Waypoint",
      totalInflow: "Total inflow",
      totalOutflow: "Total outflow",
      ioTip:
        "Waypoint inflow/outflow only counts transactions within matched paths",
      txCount: "Tx count",
      txCountTip: "Inflow count/Outflow count",
      action: "Action",
      graphAnalysis: "Graph Analysis",
      empty: "No data",
    },
  },
  alternation: {
    newOldStop: "New-Old Stop Model",
    singleChannel: "Single Channel Transfer Model",
    form: {
      addressLabel: "Analysis Address",
      addressPlaceholder: "Enter address",
      currencyLabel: "Currency Selection",
      currencyPlaceholder: "Select currency",
      directionLabel: "Direction",
      directionPlaceholder: "Select direction",
      directionOptions: {
        INFLOW: "Inflow",
        OUTFLOW: "Outflow",
      },
      amountLabel: "Transaction Amount",
      levelLabel: "Maximum Tracing Level",
      levelPlaceholder: "Select level",
      levelRange: "Association level must be between 1-20",
      endTimeLabel: "Tracing Deadline",
      endTimePlaceholder: "Select date and time",
    },
    history: {
      add: "Add",
      direction: "Direction:",
      submitTime: "Submit Time:",
      status: {
        computing: "Computing",
        success: "Success",
        failed: "Failed",
      },
    },
    result: {
      computing: "Computing",
      computingTip: "Please wait for completion to view details",
      failed: "Computation Failed",
      failedTip: "System error, please try again later",
      calculateAddress: "[Calculation Address]",
      completionTime: "[Completion Time]",
      calculationCurrency: "[Calculation Currency]",
      calculationDirection: "[Calculation Direction]",
      analysisBanner: "Analysis Result: {count} related addresses involved",
      graphAnalysis: "Graph Analysis",
      relatedAddress: "Related Address",
      level: "Level",
      amount: "Amount",
      time: "Time",
      action: "Action",
      dataDetail: "Data Details",
    },
    messages: {
      computingNotice: "Computing, please wait for completion to view details",
      addFailed: "Failed to add",
    },
  },
};
